import React, { useState, useEffect } from 'react';
import { TrainingSessionResponse, SessionFocus, AgeGroup } from '../Model';
import { SessionSortOption, filterSessions, sortSessions } from './sessionFiltering';
import { useReferenceData } from '../ReferenceDataContext';

interface SessionFiltersProps {
  sessions: TrainingSessionResponse[];
  onFilteredSessionsChange: (filteredSessions: TrainingSessionResponse[]) => void;
}

export const SessionFilters: React.FC<SessionFiltersProps> = ({
  sessions,
  onFilteredSessionsChange,
}) => {
  const { referenceData } = useReferenceData();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSessionFocus, setFilterSessionFocus] = useState<SessionFocus | 'All'>('All');
  const [filterAgeGroup, setFilterAgeGroup] = useState<AgeGroup | 'All'>('All');
  const [sortBy, setSortBy] = useState<SessionSortOption>('date-desc');
  const [filteredCount, setFilteredCount] = useState(sessions.length);

  // Calculate filtered sessions and notify parent whenever filters change
  useEffect(() => {
    const filtered = sortSessions(
      filterSessions(sessions, searchQuery, filterSessionFocus, filterAgeGroup),
      sortBy
    );
    setFilteredCount(filtered.length);
    onFilteredSessionsChange(filtered);
  }, [sessions, searchQuery, filterSessionFocus, filterAgeGroup, sortBy, onFilteredSessionsChange]);

  return (
    <div className="toolbar">
      <div className="toolbar-filters">
        <div className="search-input">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="session-focus-filter" className="filter-label">Focus:</label>
          <select
            id="session-focus-filter"
            value={filterSessionFocus}
            onChange={(e) => setFilterSessionFocus(e.target.value as SessionFocus | 'All')}
            className="form-select form-select-inline"
          >
            <option value="All">All Focus Areas</option>
            {(referenceData?.sessionFocusOptions ?? []).map(focus => (
              <option key={focus} value={focus}>{focus}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="session-age-group-filter" className="filter-label">Age:</label>
          <select
            id="session-age-group-filter"
            value={filterAgeGroup}
            onChange={(e) => setFilterAgeGroup(e.target.value as AgeGroup | 'All')}
            className="form-select form-select-inline"
          >
            <option value="All">All Ages</option>
            {(referenceData?.ageGroups ?? []).map(ag => (
              <option key={ag.value} value={ag.value}>{ag.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="session-sort-by" className="filter-label">Sort:</label>
          <select
            id="session-sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SessionSortOption)}
            className="form-select form-select-inline"
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name">Name (A-Z)</option>
            <option value="time-asc">Duration (Short first)</option>
            <option value="time-desc">Duration (Long first)</option>
          </select>
        </div>

        <span className="filter-count ml-auto">
          Showing {filteredCount} of {sessions.length} sessions
        </span>
      </div>
    </div>
  );
};
